import { useState } from 'react';
import { useSelectedContract, useSelectedCampaign } from 'hooks';
import { Pages } from 'constants/menu';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  AgxAlerts,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  formatCurrency,
} from '@urbanx/agx.ui-components2';
import {
  EnsureContractInformationTasksRequestScope,
  TaskCategoryTaskType,
  useGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import {
  CampaignDetailModelState,
  ContractDepositReleaseAvailabilityState,
  Deposit,
  DepositType,
  StaffGetContractDepositsResponse,
  StaffUpdateContractDepositsRequest,
  ValueType,
  useInvalidateUseStaffGetContractDeposits,
  useStaffGetContractDeposits,
  useStaffUpdateContractDeposits,
} from '@urbanx/agx.campaigns.client';
import { useQueryClient } from '@tanstack/react-query';
import { DepositPanel } from './panels/DepositPanel';
import { AddDepositButton } from './modals/AddDepositButton';
import { CrmDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/CrmDataUpdateHelper';
import { isEqual } from 'lodash-es';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import useCrmDisconnectedModal, {
  CrmDisconnectedModalChange,
} from 'pages/campaigns/components/manageCampaign/useCrmDisconnectedModal';
import {
  getDepositExcessAmount,
  getNswBalanceDepositWithDueDate,
  getTotalDepositAmount,
  normalizeDepositToDollar,
} from './depositCalculations';
import EarlyReleaseModal from './modals/EarlyReleaseModal';
import EarlyReleaseSummary from './EarlyReleaseSummary';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';
import { useContractInformationTaskSynchronization } from './useContractInformationTaskSynchronization';
import {
  LinkedDateWarningModal,
  presentCompletedTaskConflicts,
} from './modals/LinkedDateWarningModal';
import { toDepositsTaskProposal } from './contractInformationTaskProposals';
import { isDepositWorkflowTask } from 'utils/tasks/taskWorkflow';

const earlyReleaseBlockingTaskTypes = new Set<TaskCategoryTaskType>([
  TaskCategoryTaskType.DepositExceeds10Percent,
  TaskCategoryTaskType.DepositExceeds10PercentPaymentReview,
  TaskCategoryTaskType.EarlyReleaseOfDepositPaymentReview,
  TaskCategoryTaskType.NswEarlyReleaseOfDepositPayments,
]);

const hasDepositValue = (deposit: Deposit | undefined): deposit is Deposit => {
  if (!deposit) return false;

  if (deposit.depositValueType === ValueType.Dollar) {
    return deposit.amount !== undefined && deposit.amount !== null;
  }

  if (deposit.depositValueType === ValueType.Percentage) {
    return deposit.percent !== undefined && deposit.percent !== null;
  }

  return false;
};

const getDepositReleaseData = (response: StaffGetContractDepositsResponse | undefined) => {
  if (!response) return undefined;

  // Campaigns omits empty collections from HTTP responses.
  const { depositReleases: rawDepositReleases, earlyReleaseAvailability } = response;
  const depositReleases = rawDepositReleases ?? [];
  if (
    !earlyReleaseAvailability ||
    earlyReleaseAvailability.state == null ||
    earlyReleaseAvailability.availableAmount == null
  ) {
    throw new Error('Canonical deposit release availability is required');
  }

  return {
    depositReleases,
    earlyReleaseAvailability: {
      state: earlyReleaseAvailability.state,
      availableAmount: earlyReleaseAvailability.availableAmount,
    },
  };
};

const Deposits = () => {
  const { contract, campaignId, isTaskWorkflowEnabled } = useSelectedContract();
  const { campaign } = useSelectedCampaign();
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;

  const [openEarlyReleaseModal, setOpenEarlyReleaseModal] = useState(false);
  const invalidateUseStaffGetContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const queryClient = useQueryClient();

  const {
    data: contractDeposits,
    isPending,
    isError,
  } = useStaffGetContractDeposits(contract?.contractId || '', !!contract?.contractId);

  const { modal: crmDisconnectedModal, openWithChanges } = useCrmDisconnectedModal();

  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: contractTasks, isError: contractTasksError } = useGetAutopilotTasksForCampaign(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );
  const hasOutstandingDepositTasks = contractTasks?.some((task) => {
    const taskType = task.category?.taskType;
    if (
      taskType === TaskCategoryTaskType.InitialDeposit ||
      taskType === TaskCategoryTaskType.BalanceDeposit
    )
      return false;

    return (
      task.isCompleted !== true &&
      (isDepositWorkflowTask(task) ||
        (taskType != null && earlyReleaseBlockingTaskTypes.has(taskType)))
    );
  });

  type DepositsDraft = {
    initialDeposit: Deposit | undefined;
    balanceDeposit: Deposit | undefined;
  };

  const toDraft = (deposits: StaffGetContractDepositsResponse): DepositsDraft => ({
    initialDeposit: deposits.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Initial
    ),
    balanceDeposit: deposits.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Balance
    ),
  });

  const source = contractDeposits ? toDraft(contractDeposits) : undefined;

  const taskSynchronization = useContractInformationTaskSynchronization({
    campaignId,
    contractId: contract?.contractId,
    scope: EnsureContractInformationTasksRequestScope.Deposits,
    isTaskWorkflowEnabled,
    canonicalRevision: JSON.stringify(source),
  });

  const getDepositsForSave = (draft: Readonly<DepositsDraft>) => {
    const balanceDepositForSave = isNsw
      ? getNswBalanceDepositWithDueDate({
          deposit: draft.balanceDeposit,
          dueDateString: currentInitialAndBalanceDueDates.balance,
        })
      : draft.balanceDeposit;
    const deposits = isNsw
      ? [
          normalizeDepositToDollar(draft.initialDeposit, contract?.salePrice ?? undefined),
          normalizeDepositToDollar(balanceDepositForSave, contract?.salePrice ?? undefined),
        ]
      : [draft.initialDeposit, draft.balanceDeposit];

    return deposits.filter(hasDepositValue);
  };

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:deposits`,
    source,
    isPending,
    isError,
    preflight: isTaskWorkflowEnabled
      ? async ({ draft }) => {
          const depositsProposal = toDepositsTaskProposal(getDepositsForSave(draft));
          const taskConflicts = presentCompletedTaskConflicts(
            await taskSynchronization.previewConflicts({ depositsProposal })
          );

          return taskConflicts.length > 0
            ? { impactedDates: [], sourceDateTypes: [], taskConflicts }
            : undefined;
        }
      : undefined,
    persist: async ({ baseline, draft }) => {
      const request: StaffUpdateContractDepositsRequest = {
        deposits: getDepositsForSave(draft),
      };
      const response = await staffUpdateContractDeposits({
        body: request,
        contractId: contract!.contractId,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.contractDeposits) throw new Error('Canonical contract deposits are required');

      queryClient.setQueryData<StaffGetContractDepositsResponse>(
        ['staffGetContractDeposits', contract!.contractId],
        response.contractDeposits
      );

      const changes: CrmDisconnectedModalChange[] = [];

      if (!isEqual(baseline.initialDeposit?.amount, draft.initialDeposit?.amount)) {
        changes.push({
          field: CrmDisconnectedModalFields.InitialDeposit,
          value: draft.initialDeposit?.amount,
        });
      }
      if (!isEqual(previousDueDates.initial, currentInitialAndBalanceDueDates.initial)) {
        changes.push({
          field: CrmDisconnectedModalFields.InitialDepositDueDate,
          value: currentInitialAndBalanceDueDates.initial,
        });
      }
      if (!isEqual(baseline.balanceDeposit?.amount, draft.balanceDeposit?.amount)) {
        changes.push({
          field: CrmDisconnectedModalFields.BalanceDeposit,
          value: draft.balanceDeposit?.amount,
        });
      }
      if (!isEqual(previousDueDates.balance, currentInitialAndBalanceDueDates.balance)) {
        changes.push({
          field: CrmDisconnectedModalFields.BalanceDepositDueDate,
          value: currentInitialAndBalanceDueDates.balance,
        });
      }

      openWithChanges(changes);

      return { accepted: true, baseline: toDraft(response.contractDeposits) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () => invalidateUseStaffGetContractDeposits(contract!.contractId),
    },
    synchronization: taskSynchronization.synchronization,
  });

  const previousInitialDueDate = useFormConditionDueDate(source?.initialDeposit);
  const previousBalanceDueDate = useFormConditionDueDate(source?.balanceDeposit);
  const currentInitialDueDate = useFormConditionDueDate(editor.draft?.initialDeposit);
  const currentBalanceDueDate = useFormConditionDueDate(editor.draft?.balanceDeposit);
  const previousDueDates = {
    initial: previousInitialDueDate.dueDateString,
    balance: previousBalanceDueDate.dueDateString,
  };
  const currentInitialAndBalanceDueDates = {
    initial: currentInitialDueDate.dueDateString,
    balance: currentBalanceDueDate.dueDateString,
  };

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.DEPOSITS} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { initialDeposit, balanceDeposit } = editor.draft;
  const totalDeposit = getTotalDepositAmount({
    initialDeposit,
    balanceDeposit,
    salePrice: contract?.salePrice ?? undefined,
  });
  const depositExcessAmount = getDepositExcessAmount({
    totalDepositAmount: totalDeposit,
    salePrice: contract?.salePrice,
  });
  const hasInitialDepositValue = hasDepositValue(initialDeposit);
  const hasBalanceDepositValue = hasDepositValue(balanceDeposit);
  const canAddDeposit =
    !hasInitialDepositValue || (isNsw && (!hasInitialDepositValue || !hasBalanceDepositValue));
  const depositReleaseData = getDepositReleaseData(contractDeposits);
  const earlyReleaseAvailability = depositReleaseData?.earlyReleaseAvailability;

  if (openEarlyReleaseModal && !earlyReleaseAvailability) {
    throw new Error('Deposit release availability is required to open the early release modal');
  }

  const earlyReleaseAvailabilityMessage = (() => {
    if (contractTasksError)
      return 'Unable to confirm deposit review tasks. Reload before releasing funds.';
    if (contractTasks == null) return 'Checking deposit review tasks before releasing funds.';
    if (hasOutstandingDepositTasks)
      return 'Complete the outstanding deposit review and payment tasks before releasing funds.';

    switch (earlyReleaseAvailability?.state) {
      case ContractDepositReleaseAvailabilityState.Available:
        return `${formatCurrency(earlyReleaseAvailability.availableAmount, 2)} is available for early release.`;
      case ContractDepositReleaseAvailabilityState.AwaitingInitialDeposit:
        return 'Early release is unavailable until the initial deposit has been received.';
      case ContractDepositReleaseAvailabilityState.AwaitingAdditionalDeposit:
        return `Early release is unavailable until an additional deposit has been received. ${formatCurrency(earlyReleaseAvailability.availableAmount, 2)} is currently confirmed.`;
      case ContractDepositReleaseAvailabilityState.FullyReleased:
        return 'All received deposit funds have already been released.';
      case ContractDepositReleaseAvailabilityState.Ineligible:
        return earlyReleaseAvailability.availableAmount > 0
          ? `Early release is currently unavailable. ${formatCurrency(earlyReleaseAvailability.availableAmount, 2)} in received deposit funds remains unreleased.`
          : 'Early release is currently unavailable.';
      default:
        return undefined;
    }
  })();

  return (
    <ContractInformationPage currentPage={Pages.DEPOSITS} editor={editor}>
      {!isPending ? (
        <>
          <AgxSpacer direction="vertical" fill gap={'2xs'}>
            {depositExcessAmount != null && depositExcessAmount > 0 && (
              <AgxAlerts
                alerts={[
                  {
                    alertType: 'Error',
                    heading: `Deposit exceeds 10% of the sale price by ${formatCurrency(depositExcessAmount, 2)}`,
                    id: 'depositExceedErrorAlert',
                    isDismissed: false,
                    title: '',
                    description: '',
                    solveElementId: '',
                  },
                ]}
                id={'depositExeedsAlert'}
                dismissedAlertIds={[]}
                onValueChanged={() => {}}
              />
            )}
            {!hasInitialDepositValue && !hasBalanceDepositValue && (
              <SubPagePanel expandable={false}>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-md"
                  contentAlignment="center"
                  contentJustification="center"
                  fill
                  padding={{ top: 'spacing-8xl', bottom: 'spacing-8xl' }}
                >
                  <AgxTypography id="no-deposits" type="body_ui" size="medium">
                    No deposits added.
                  </AgxTypography>
                </AgxSpacer>
              </SubPagePanel>
            )}
            <AgxSpacer direction="vertical" fill gap={'md'}>
              {hasInitialDepositValue && (
                <DepositPanel
                  id="initial-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Initial Deposit"
                  deposit={initialDeposit}
                  onChange={(value) =>
                    editor.update('initialDeposit', value as Deposit | undefined)
                  }
                />
              )}
              {hasBalanceDepositValue && (
                <DepositPanel
                  id="balance-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Balance Deposit"
                  deposit={balanceDeposit}
                  onChange={(value) =>
                    editor.update('balanceDeposit', value as Deposit | undefined)
                  }
                />
              )}
              {depositReleaseData?.depositReleases.length && campaign?.state != null ? (
                <EarlyReleaseSummary
                  campaignState={campaign.state}
                  depositReleases={depositReleaseData.depositReleases}
                />
              ) : null}
            </AgxSpacer>
          </AgxSpacer>
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            contentJustification="start"
            contentAlignment="start"
          >
            {canAddDeposit && (
              <AddDepositButton
                id="initial-deposit"
                label={isNsw ? 'Add Deposit' : 'Add Initial Deposit'}
                depositType={DepositType.Initial}
                onAddDeposits={(initial, balance) => {
                  editor.update((current) => ({
                    ...current,
                    initialDeposit: initial,
                    ...(isNsw || balance ? { balanceDeposit: balance } : {}),
                  }));
                }}
              />
            )}
            {!isNsw && !hasBalanceDepositValue && (
              <AddDepositButton
                id="balance-deposit"
                label="Add Balance Deposit"
                depositType={DepositType.Balance}
                onAddDeposits={(_, balance) => {
                  if (balance) {
                    editor.update('balanceDeposit', balance);
                  }
                }}
                disabled={!hasInitialDepositValue}
              />
            )}
            <AgxButton
              id="early-release"
              text="Early Release"
              variant="primary"
              danger
              disabled={
                contractTasks == null ||
                contractTasksError ||
                hasOutstandingDepositTasks ||
                earlyReleaseAvailability?.state !==
                  ContractDepositReleaseAvailabilityState.Available
              }
              onClick={() => setOpenEarlyReleaseModal(true)}
            />
          </AgxSpacer>
          {earlyReleaseAvailabilityMessage && (
            <AgxTypography id="early-release-availability" type="body_ui" size="small">
              {earlyReleaseAvailabilityMessage}
            </AgxTypography>
          )}
        </>
      ) : undefined}
      {openEarlyReleaseModal && earlyReleaseAvailability && (
        <EarlyReleaseModal
          isOpen={openEarlyReleaseModal}
          availableAmount={earlyReleaseAvailability.availableAmount}
          onClose={() => setOpenEarlyReleaseModal(false)}
        />
      )}
      {editor.confirmation && (
        <LinkedDateWarningModal
          impactedDates={editor.confirmation.impactedDates}
          sourceDateTypes={editor.confirmation.sourceDateTypes}
          taskConflicts={editor.confirmation.taskConflicts}
          onCancel={editor.cancelSave}
          onConfirm={editor.confirmSave}
        />
      )}
      {crmDisconnectedModal}
    </ContractInformationPage>
  );
};

export default Deposits;
